/**
 * API Logger Utility
 * 
 * Centralized logging for all API requests and responses.
 * Logs full URL, request details, and response details.
 */

interface ApiLogEntry {
  timestamp: string;
  method: string;
  url: string;
  request: {
    headers: Record<string, string>;
    body?: unknown;
  };
  response: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body?: unknown;
  };
  duration: number;
  error?: string;
}

/**
 * Sanitize headers to remove sensitive information
 */
function sanitizeHeaders(headers: HeadersInit): Record<string, string> {
  const sanitized: Record<string, string> = {};
  
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      const lowerKey = key.toLowerCase();
      if (lowerKey === 'authorization') {
        // Only show first few characters of token
        const token = value as string;
        sanitized[key] = token.substring(0, 20) + '...';
      } else {
        sanitized[key] = value;
      }
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      if (lowerKey === 'authorization') {
        const token = value as string;
        sanitized[key] = token.substring(0, 20) + '...';
      } else {
        sanitized[key] = value;
      }
    });
  } else {
    Object.entries(headers).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      if (lowerKey === 'authorization') {
        const token = value as string;
        sanitized[key] = token.substring(0, 20) + '...';
      } else {
        sanitized[key] = value as string;
      }
    });
  }
  
  return sanitized;
}

/**
 * Parse response headers
 */
function parseResponseHeaders(headers: Headers): Record<string, string> {
  const parsed: Record<string, string> = {};
  headers.forEach((value, key) => {
    parsed[key] = value;
  });
  return parsed;
}

/**
 * Clone and parse response body for logging
 */
async function cloneResponseForLogging(response: Response): Promise<unknown> {
  try {
    const contentType = response.headers.get('content-type') || '';
    
    // Only parse JSON responses
    if (contentType.includes('application/json')) {
      const clonedResponse = response.clone();
      const text = await clonedResponse.text();
      if (text) {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      }
    }
    
    // For non-JSON, return a placeholder
    return '[Non-JSON response]';
  } catch {
    return '[Unable to parse response]';
  }
}

/**
 * Logged fetch wrapper that logs all API requests and responses
 */
export async function loggedFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const startTime = Date.now();
  const timestamp = new Date().toISOString();
  const method = options.method || 'GET';
  
  // Parse request body
  let requestBody: unknown = undefined;
  if (options.body) {
    try {
      if (typeof options.body === 'string') {
        requestBody = JSON.parse(options.body);
      } else {
        requestBody = options.body;
      }
    } catch {
      requestBody = '[Unable to parse request body]';
    }
  }
  
  // Sanitize headers
  const sanitizedHeaders = sanitizeHeaders(options.headers || {});
  
  // Log request
  const logEntry: ApiLogEntry = {
    timestamp,
    method,
    url,
    request: {
      headers: sanitizedHeaders,
      body: requestBody,
    },
    response: {
      status: 0,
      statusText: '',
      headers: {},
    },
    duration: 0,
  };
  
  try {
    // Make the actual fetch request
    const response = await fetch(url, options);
    const duration = Date.now() - startTime;
    
    // Parse response
    const responseBody = await cloneResponseForLogging(response);
    const responseHeaders = parseResponseHeaders(response.headers);
    
    // Update log entry with response
    logEntry.response = {
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      body: responseBody,
    };
    logEntry.duration = duration;
    
    // Log the complete API call
    console.log('[API Request]', {
      timestamp: logEntry.timestamp,
      method: logEntry.method,
      url: logEntry.url,
      request: logEntry.request,
      response: logEntry.response,
      duration: `${logEntry.duration}ms`,
    });
    
    return response;
  } catch (error) {
    const duration = Date.now() - startTime;
    logEntry.duration = duration;
    logEntry.error = error instanceof Error ? error.message : String(error);
    
    // Log the error
    console.error('[API Request Error]', {
      timestamp: logEntry.timestamp,
      method: logEntry.method,
      url: logEntry.url,
      request: logEntry.request,
      error: logEntry.error,
      duration: `${logEntry.duration}ms`,
    });
    
    throw error;
  }
}
